import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { patch, post } from "@/lib/api";
import type { Approval, DocumentDetail, DocumentInput } from "@/lib/types";

const AUTOSAVE_MS = 600;

export const documentKey = (id: number) => ["document", id] as const;

/**
 * Writes to one document. Calls run one after another, so a slow response can never overwrite a newer one; every
 * response is the full detail and replaces the cached one. Reads of the document in flight are cancelled first.
 */
export function useDocumentWrites(id: number) {
  const qc = useQueryClient();
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  const run = useCallback(
    (call: () => Promise<DocumentDetail>): Promise<DocumentDetail> => {
      // A GET still running (e.g. a refetch on window focus) could answer with the state before this write.
      const next = queue.current.then(async () => {
        await qc.cancelQueries({ queryKey: documentKey(id) });
        return call();
      });
      queue.current = next.catch(() => undefined);
      return next.then(async (detail) => {
        await qc.cancelQueries({ queryKey: documentKey(id) });
        qc.setQueryData(documentKey(id), detail);
        qc.invalidateQueries({ queryKey: ["documents"] });
        return detail;
      });
    },
    [id, qc],
  );

  return {
    save: useCallback((input: DocumentInput) => run(() => patch<DocumentDetail>(`/api/documents/${id}`, input)), [id, run]),
    addItems: useCallback(
      (items: { timeEntryIds?: number[]; expenseShareIds?: number[] }) => run(() => post<DocumentDetail>(`/api/documents/${id}/items`, items)),
      [id, run],
    ),
    omitShare: useCallback(
      (expenseShareId: number, reason: string) => run(() => post<DocumentDetail>(`/api/documents/${id}/omissions`, { expenseShareId, reason })),
      [id, run],
    ),
    rebuildDeductions: useCallback(() => run(() => post<DocumentDetail>(`/api/documents/${id}/rebuild-deductions`)), [id, run]),
  };
}

export type DocumentWrites = ReturnType<typeof useDocumentWrites>;

/**
 * One autosaved field: keeps what was typed, saves 600 ms after the last keystroke or on blur, and never sends the same
 * value twice while it is on its way. The server's value is taken while the field is clean and not being edited, so a
 * saved „250“ comes back as „250,00“ once the field is left; a failed save keeps the text and reports the error.
 */
export function useAutosave<T>(server: T, save: (value: T) => Promise<unknown>) {
  const [value, setValue] = useState(server);
  const [error, setError] = useState<string | null>(null);
  // Bumped after each own save: adopt the server value even when it did not change (or changed before we were clean).
  const [settled, setSettled] = useState(0);
  const serverRef = useRef(server);
  serverRef.current = server;
  const latest = useRef(server);
  const dirty = useRef(false);
  const editing = useRef(false);
  const inFlight = useRef<{ value: T; done: Promise<void> } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (dirty.current || editing.current) return;
    latest.current = serverRef.current;
    setValue(serverRef.current);
  }, [server, settled]);

  const flush = useCallback((): Promise<void> => {
    clearTimeout(timer.current);
    if (!dirty.current) return Promise.resolve();
    const sent = latest.current;
    if (inFlight.current && Object.is(inFlight.current.value, sent)) return inFlight.current.done;
    const done = (async () => {
      try {
        await saveRef.current(sent);
        if (Object.is(latest.current, sent)) dirty.current = false;
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        if (inFlight.current?.value === sent) inFlight.current = null;
        setSettled((n) => n + 1);
      }
    })();
    inFlight.current = { value: sent, done };
    return done;
  }, []);

  const change = useCallback(
    (next: T) => {
      latest.current = next;
      dirty.current = true;
      setValue(next);
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, AUTOSAVE_MS);
    },
    [flush],
  );

  const focus = useCallback(() => {
    editing.current = true;
  }, []);

  const blur = useCallback(() => {
    editing.current = false;
    void flush();
    setSettled((n) => n + 1);
  }, [flush]);

  // Leaving the page with a pending keystroke still saves it.
  useEffect(() => () => void flush(), [flush]);

  return { value, change, focus, blur, error };
}

/** After an action: the document and everything listing it are fetched again. */
export function useRefreshDocument(id: number) {
  const qc = useQueryClient();
  return useCallback(
    () => Promise.all([qc.invalidateQueries({ queryKey: documentKey(id) }), qc.invalidateQueries({ queryKey: ["documents"] })]).then(() => undefined),
    [id, qc],
  );
}

/** Florian's decision on an agent's request; both refresh the document whatever the outcome. */
export function useApprovalDecision(approval: Pick<Approval, "id" | "documentId">) {
  const refreshDocument = useRefreshDocument(approval.documentId);
  const qc = useQueryClient();
  // A settled request must leave „Braucht dich“ at once.
  const refresh = useCallback(() => Promise.all([refreshDocument(), qc.invalidateQueries({ queryKey: ["approvals"] })]).then(() => undefined), [refreshDocument, qc]);
  const approve = useMutation({
    mutationFn: (overrides: Record<string, unknown>) => post<Approval>(`/api/approvals/${approval.id}/approve`, overrides),
    onSettled: refresh,
  });
  const reject = useMutation({
    mutationFn: (reason: string) => post<Approval>(`/api/approvals/${approval.id}/reject`, { reason }),
    onSettled: refresh,
  });
  return { approve, reject };
}
